import { AloftDate, AloftPrice } from '@aloft/toolkit-shared';

import { CompSelectionProperty, CompSelectionState } from '../types.ts';
import { isAreaBracketed, isNumberBracketed } from './bracketing.ts';

export const getSelectedIDs = (
  rowSelection: CompSelectionState,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
): string[] => {
  if (isInspectifyOrder && hasArvOption) {
    return Object.entries(rowSelection)
      .filter(([, value]) => value.aiv || value.arv)
      .map(([key]) => key);
  }

  return Object.keys(rowSelection);
};

const averageSalePrice = (prices: AloftPrice[]): AloftPrice | undefined => {
  if (prices.length === 0) return undefined;
  return AloftPrice.add(...prices).div(prices.length);
};

type SelectedCompStats = {
  selectedCount: number;
  aivAvgSalePrice?: AloftPrice;
  arvAvgSalePrice?: AloftPrice;
  difference?: AloftPrice;
};

export const getSelectedCompStats = ({
  rowSelection,
  properties,
  isInspectifyOrder,
  hasArvOption,
}: {
  rowSelection: CompSelectionState;
  properties: CompSelectionProperty[];
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
}): SelectedCompStats => {
  const selectedIDs = getSelectedIDs(rowSelection, isInspectifyOrder, hasArvOption).filter(
    (id) => id !== 'subject',
  );

  if (!hasArvOption) return { selectedCount: selectedIDs.length };

  const propertiesById = new Map(properties.map((property) => [property.id, property]));

  const aivPrices: AloftPrice[] = [];
  const arvPrices: AloftPrice[] = [];

  for (const id of selectedIDs) {
    const selection = rowSelection[id];
    const salePrice = propertiesById.get(id)?.salePrice;
    if (!salePrice || !selection) continue;
    if (selection.aiv) aivPrices.push(salePrice);
    if (selection.arv) arvPrices.push(salePrice);
  }

  const aivAvgSalePrice = averageSalePrice(aivPrices);
  const arvAvgSalePrice = averageSalePrice(arvPrices);
  const difference =
    aivAvgSalePrice && arvAvgSalePrice ? arvAvgSalePrice.sub(aivAvgSalePrice) : undefined;

  return {
    selectedCount: selectedIDs.length,
    aivAvgSalePrice,
    arvAvgSalePrice,
    difference,
  };
};

type BracketingStats = {
  glaBracketed: boolean;
  siteBracketed: boolean;
  ageBracketed: boolean;
};

const getAge = (property: CompSelectionProperty, effectiveDate: AloftDate): number | undefined =>
  property.yearBuilt !== undefined ? effectiveDate.getYear() - property.yearBuilt : undefined;

export const getBracketingStats = ({
  rowSelection,
  properties,
  subject,
  isInspectifyOrder,
  hasArvOption,
  effectiveDate,
}: {
  rowSelection: CompSelectionState;
  properties: CompSelectionProperty[];
  subject: CompSelectionProperty;
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
  effectiveDate: AloftDate;
}): BracketingStats => {
  const selectedIDs = getSelectedIDs(rowSelection, isInspectifyOrder, hasArvOption).filter(
    (id) => id !== 'subject',
  );

  const propertiesById = new Map(properties.map((property) => [property.id, property]));
  const selectedComps = selectedIDs
    .map((id) => propertiesById.get(id))
    .filter((property): property is CompSelectionProperty => property !== undefined);

  const compGLAs = selectedComps.map((comp) => comp.gla).filter((gla) => gla !== undefined);
  const compSiteSizes = selectedComps
    .map((comp) => comp.lotSize)
    .filter((lotSize) => lotSize !== undefined);
  const compAges = selectedComps
    .map((comp) => getAge(comp, effectiveDate))
    .filter((age): age is number => age !== undefined);

  return {
    glaBracketed: isAreaBracketed(subject.gla, compGLAs),
    siteBracketed: isAreaBracketed(subject.lotSize, compSiteSizes),
    ageBracketed: isNumberBracketed(getAge(subject, effectiveDate), compAges),
  };
};
